import { useTranslation } from "react-i18next";
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

interface TreemapDataItem {
  name: string;
  domain?: string;
  domainId?: string;
  value: number;
  percentage: number;
  color?: string;
}

interface TreemapChartProps {
  data: TreemapDataItem[];
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  height?: number;
  onItemClick?: (item: TreemapDataItem) => void;
}

// 预定义颜色方案（参考截图风格，稳定映射）
const COLOR_SCHEME = [
  "#239B56", // 蓝宝石色
  "#F77F00", // 橙色
  "#0077B6", // 深蓝色
  "#E63946", // 红色
  "#2A9D8F", // 青绿色
  "#9D4EDD", // 紫色
  "#F15BB5", // 粉红色
  "#00BBF9", // 天蓝色
  "#FB5607", // 橙红色
  "#8AC926", // 黄绿色
];

const TreemapChart: React.FC<TreemapChartProps> = ({
  data,
  title,
  className,
  style,
  height = 400,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("common.charts.treemap_works_dist");
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || !data.length) return;

    // 初始化图表
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // 处理数据：在纯色基础上加入非常轻微的纵向渐变（顶部略深、底部略浅）
    const processedData = data.map((item, index) => {
      const baseColor = item.color || COLOR_SCHEME[index % COLOR_SCHEME.length];

      // 使用 ECharts 的 color.lift 做轻微明度调整，避免"夸张"效果
      const colorUtil: any = (echarts as any).color;
      const topColor =
        colorUtil && colorUtil.lift
          ? colorUtil.lift(baseColor, 0.0)
          : baseColor; // 略深
      const bottomColor =
        colorUtil && colorUtil.lift
          ? colorUtil.lift(baseColor, 0.08)
          : baseColor; // 略浅
      const subtleVerticalGradient = new (
        echarts as any
      ).graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: topColor },
        { offset: 1, color: bottomColor },
      ]);

      return {
        name: item.name,
        value: item.value,
        itemStyle: {
          color: subtleVerticalGradient,
        },
        // 保存额外数据用于 tooltip 和 label
        domain: item.domain,
        domainId: item.domainId,
        percentage: item.percentage,
      };
    });

    // 配置选项
    const option: echarts.EChartsOption = {
      tooltip: {
        show: false, // 完全禁用 tooltip 避免移动端错误
      },
      series: [
        {
          type: "treemap",
          data: processedData,
          roam: false,
          nodeClick: "zoomToNode", // 启用点击行为
          breadcrumb: {
            show: false,
          },
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          label: {
            show: true,
            // 恢复为：名称 + 域名 + 百分比（三行展示），域名去掉 www 和末尾的 com/cn 等后缀
            formatter: (params: any) => {
              const { name, data } = params;
              if (data && data.domain) {
                let d = String(data.domain || "");
                // 去协议
                d = d.replace(/^https?:\/\/(www\.)?/i, "");
                // 去 www.
                d = d.replace(/^www\./i, "");
                // 去末尾的 .com/.cn/.com.cn/.cn.com 以及可能的斜杠
                d = d.replace(/\.(com|cn)(\.\w+)?$/i, "");
                d = d.replace(/\/$/, "");
                return `{name|${name}}\n{domain|${d}}\n{percent|${data.percentage}%}`;
              }
              return `{name|${name}}\n{percent|${data.percentage}%}`;
            },
            rich: {
              name: {
                fontSize: 14,
                fontWeight: "bold",
                color: "#ffffff",
                align: "left",
                verticalAlign: "top",
                padding: [2, 0, 0, 2],
              },
              // 第二行和第三行整体向右移动 1px：左内边距 3
              domain: {
                fontSize: 14,
                fontWeight: "bold",
                color: "#ffffff",
                padding: [0, 0, 0, 3],
              },
              percent: {
                fontSize: 14,
                fontWeight: "bold",
                color: "#ffffff",
                padding: [0, 0, 0, 3],
              },
            },
            fontSize: 14,
            fontWeight: "bold",
            color: "rgba(255,255,255,0.95)",
            lineHeight: 18,
          },
          emphasis: {
            itemStyle: {
              borderWidth: 0,
              //shadowBlur: 10,
              //shadowColor: 'rgba(0,0,0,0.08)'
            },
            label: {
              fontSize: 14,
            },
          },
          itemStyle: {
            //borderColor: 'rgba(255,255,255,0.35)',
            borderWidth: 1,
          },
          // 添加移动端优化配置
          animationDuration: 300,
          animationDurationUpdate: 300,
          selectedMode: false,
        },
      ],
    };

    // 设置配置
    chartInstance.current.setOption(option, true);

    // 清理之前的事件监听器
    chartInstance.current.off("click");
    chartInstance.current.off("touchstart");

    // 添加点击事件
    if (onItemClick) {
      chartInstance.current.on("click", (params: any) => {
        console.log("ECharts click event triggered:", params);
        if (
          params.componentType === "series" &&
          params.seriesType === "treemap" &&
          params.data
        ) {
          console.log(
            "Treemap item clicked, calling onItemClick with data:",
            params.data
          );
          // 阻止事件冒泡
          params.event?.stopPropagation?.();
          onItemClick(params.data);
        }
      });

      // 添加触摸事件支持
      chartInstance.current.on("touchstart", (params: any) => {
        console.log("ECharts touchstart event triggered:", params);
        if (
          params.componentType === "series" &&
          params.seriesType === "treemap" &&
          params.data
        ) {
          console.log(
            "Treemap item touched, calling onItemClick with data:",
            params.data
          );
          // 阻止事件冒泡
          params.event?.stopPropagation?.();
          onItemClick(params.data);
        }
      });
    }

    // 响应式处理
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    // 清理 resize 事件监听器
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data, effectiveTitle, onItemClick]);

  // 清理
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: `${height}px`,
        touchAction: "manipulation", // 改善移动端触摸体验
        userSelect: "none", // 防止文本选择干扰点击
        ...style,
      }}
    >
      <div
        ref={chartRef}
        className={className}
        style={{
          width: "100%",
          height: "100%",
          cursor: "pointer", // 显示可点击光标
        }}
      />
    </div>
  );
};

export default TreemapChart;
